import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import WebsiteLayout from '../components/WebsiteLayout';
import Reveal from '../components/Reveal';
import { useLanguage } from '../../../hooks/useLanguage';

const FOUNDERS_ZIP_URL =
  'https://thoughtfuloasis-public.s3.us-east-1.amazonaws.com/founders.zip';
const WORDMARK_SVG_URL = '/assets/website/brand/wordmark.svg';

const Brand: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';

  const customCta = (
    <div className="site-end__cta">
      <span className="site-end__label">{en ? 'Talk to us' : 'Praat met ons'}</span>
      <h2 className="site-end__title">
        {en
          ? "Need a document or asset not listed here? Ask and we'll send."
          : 'Een document of asset nodig dat hier niet staat? Vraag het, we sturen het.'}
      </h2>
      <div className="site-end__action">
        <Link className="btn btn-primary" to="/contact?prefill=brand">
          {en ? 'Contact us' : 'Neem contact op'}
        </Link>
      </div>
    </div>
  );

  const downloadWordmark = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    fetch(WORDMARK_SVG_URL)
      .then((r) => r.text())
      .then((text) => {
        const blob = new Blob([text], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Thoughtful Oasis.svg';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
  };

  return (
    <WebsiteLayout customCta={customCta}>
      <Helmet>
        <title>Documents · Thoughtful Oasis</title>
        <meta
          name="description"
          content="Document centre for Thoughtful Oasis: privacy policy, terms, data processing agreement, sub-processors, brand assets and press kit."
        />
        <link rel="canonical" href="https://thoughtful-oasis.com/documents" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Documents · Thoughtful Oasis" />
        <meta
          property="og:description"
          content="Legal, compliance, brand and press documents in one place. For our security posture see the trust center."
        />
        <meta property="og:url" content="https://thoughtful-oasis.com/documents" />
        <meta property="og:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
      </Helmet>

      <header className="hero hero--sub">
        <div className="hero__inner">
          <span className="label">{en ? 'Documents' : 'Documenten'}</span>
          <h1 style={{ marginTop: 20 }}>
            {en ? 'Documents, in one place.' : 'Documenten, op één plek.'}
          </h1>
          <p className="hero__sub">
            {en ? (
              <>
                Legal, compliance, brand and press documents in one place. For our
                security posture, certifications, incident response and EU AI Act
                stance, see our trust center at{' '}
                <a
                  className="link-inline"
                  href="https://trust.thoughtful-oasis.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  trust.thoughtful-oasis.com
                </a>
                .
              </>
            ) : (
              <>
                Juridische, compliance-, brand- en persdocumenten op één plek. Voor onze
                security-positie, certificeringen, incident response en positie ten
                aanzien van de EU AI-verordening, zie ons trust center op{' '}
                <a
                  className="link-inline"
                  href="https://trust.thoughtful-oasis.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  trust.thoughtful-oasis.com
                </a>
                .
              </>
            )}
          </p>
        </div>
      </header>

      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">{en ? 'Documents' : 'Documenten'}</span>
            <h2 style={{ marginTop: 18 }}>
              {en ? 'Current versions.' : 'Actuele versies.'}
            </h2>
            <p className="lede" style={{ marginTop: 24 }}>
              {en
                ? 'Legal and compliance documents first, then brand and press assets. Each is the version currently in force; older versions are kept on file and available on request.'
                : 'Juridische en compliance-documenten eerst, dan brand- en persmateriaal. Elk is de versie die nu van kracht is; oudere versies worden bewaard en zijn op verzoek beschikbaar.'}
            </p>
          </Reveal>

          <div className="download-list">
            <Reveal className="download-list__item">
              <div>
                <p className="download-list__title">
                  {en ? 'Privacy Policy' : 'Privacybeleid'}
                </p>
                <p className="download-list__desc">
                  {en
                    ? 'How we collect, process and retain personal data, the lawful basis we operate under, and the rights data subjects have.'
                    : 'Hoe we persoonsgegevens verzamelen, verwerken en bewaren, op welke rechtsgrondslag we werken, en welke rechten betrokkenen hebben.'}
                </p>
                <p className="download-list__meta">
                  {en ? 'Current version · EN & NL' : 'Actuele versie · EN & NL'}
                </p>
              </div>
              <Link className="paper-list__link" to="/privacy-policy">
                {en ? 'Read' : 'Lezen'}
              </Link>
            </Reveal>

            <Reveal className="download-list__item">
              <div>
                <p className="download-list__title">
                  {en ? 'Terms of Service' : 'Algemene voorwaarden'}
                </p>
                <p className="download-list__desc">
                  {en
                    ? 'The terms under which Thoughtful Oasis services are made available. Includes acceptable use, warranties, and liability framing.'
                    : 'De voorwaarden waaronder de diensten van Thoughtful Oasis beschikbaar worden gesteld. Inclusief acceptabel gebruik, garanties en aansprakelijkheidsframework.'}
                </p>
                <p className="download-list__meta">
                  {en ? 'Current version · EN & NL' : 'Actuele versie · EN & NL'}
                </p>
              </div>
              <Link className="paper-list__link" to="/tos">
                {en ? 'Read' : 'Lezen'}
              </Link>
            </Reveal>

            <Reveal className="download-list__item">
              <div>
                <p className="download-list__title">{en ? 'Press kit' : 'Perskit'}</p>
                <p className="download-list__desc">
                  {en
                    ? 'Boilerplate at three lengths (one-liner, three sentences, paragraph), key facts, founder bios and selected press. Bilingual EN / NL. For journalists, partner pages and conference programmes.'
                    : "Boilerplate in drie lengtes (oneliner, drie zinnen, paragraaf), key facts, founders-bio's en geselecteerde pers. Tweetalig EN / NL. Voor journalisten, partnerpagina's en conferentieprogramma's."}
                </p>
                <p className="download-list__meta">
                  {en ? 'HTML · open in browser' : 'HTML · openen in browser'}
                </p>
              </div>
              <Link className="paper-list__link" to="/documents/press-kit">
                {en ? 'Open' : 'Openen'}
              </Link>
            </Reveal>

            <Reveal className="download-list__item">
              <div>
                <p className="download-list__title">Wordmark</p>
                <p className="download-list__desc">
                  {en
                    ? 'The Thoughtful Oasis wordmark as a scalable vector, the same typesetting used in the site navigation. Drop into articles, partner pages and conference programmes.'
                    : "De Thoughtful Oasis-wordmark als schaalbare vector, in dezelfde belettering als de site-navigatie. Voor artikelen, partnerpagina's en conferentieprogramma's."}
                </p>
                <p className="download-list__meta">
                  {en ? 'SVG · ink on transparent' : 'SVG · inkt op transparant'}
                </p>
              </div>
              <div className="paper-list__actions">
                <a className="paper-list__link" href="#" onClick={downloadWordmark}>
                  {en ? 'Download' : 'Downloaden'}
                </a>
                <a
                  className="paper-list__link paper-list__link--quiet"
                  href={WORDMARK_SVG_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {en ? 'View' : 'Bekijken'}
                </a>
              </div>
            </Reveal>

            <Reveal className="download-list__item">
              <div>
                <p className="download-list__title">
                  {en ? 'Founder photos' : "Foto's van de oprichters"}
                </p>
                <p className="download-list__desc">
                  {en
                    ? 'High-resolution headshots of the three founders plus a team shot. Use for press, conference programmes and partner pages.'
                    : "Headshots in hoge resolutie van de drie oprichters plus een teamfoto. Te gebruiken voor pers, conferentieprogramma's en partnerpagina's."}
                </p>
                <p className="download-list__meta">
                  {en ? 'ZIP · 4 × JPG' : 'ZIP · 4 × JPG'}
                </p>
              </div>
              <a className="paper-list__link" href={FOUNDERS_ZIP_URL} download>
                {en ? 'Download' : 'Downloaden'}
              </a>
            </Reveal>

            <Reveal className="download-list__item">
              <div>
                <p className="download-list__title">
                  {en ? 'News & press' : 'Nieuws & pers'}
                </p>
                <p className="download-list__desc">
                  {en
                    ? 'Announcements, articles, podcasts and features that mention Thoughtful Oasis. Use these to back up press writing about us.'
                    : 'Aankondigingen, artikelen, podcasts en stukken waarin Thoughtful Oasis genoemd wordt. Te gebruiken als onderbouwing voor pers-content over ons.'}
                </p>
                <p className="download-list__meta">
                  {en ? 'Articles · external links' : 'Artikelen · externe links'}
                </p>
              </div>
              <Link className="paper-list__link" to="/news">
                {en ? 'Visit news' : 'Bekijk nieuws'}
              </Link>
            </Reveal>
          </div>

          <p
            className="body-text"
            style={{
              margin: '56px auto 0',
              maxWidth: 'var(--max-w-prose)',
              padding: '0 var(--pad-x)',
              textAlign: 'center',
            }}
          >
            {en ? 'Need something not listed? ' : 'Iets nodig wat hier niet staat? '}
            <Link className="link-inline" to="/contact?prefill=brand">
              {en ? "Ask and we'll send it" : 'Vraag het, we sturen het'}
            </Link>
            .
          </p>
        </div>
      </section>
    </WebsiteLayout>
  );
};

export default Brand;
